export const WALTER_PROTOCOL = 4 as const;
export const WALTER_STATES = ["idle", "aware", "listening", "thinking", "tool", "speaking", "sleeping"] as const;
export const EMOTIONS = ["serene", "curious", "focused", "amused", "concerned"] as const;
export const ENVIRONMENTS = ["walter", "vision", "news", "system"] as const;
export const DISPLAY_ROLES = ["main", "third-eye", "heart", "simulator"] as const;
export const TOOL_TYPES = ["memory", "vision", "network", "message", "system", "timer", "news"] as const;
export const WALTER_PHASES = ["idle", "attending", "listening", "transcribing", "thinking", "tool-running", "speaking", "recovering"] as const;
export const DEVICE_ROLES = ["main", "third-eye", "heart", "mic", "camera", "speaker", "brain"] as const;
export const WALTER_SURFACES = ["presence", "talk", "vision", "memory", "tools", "news", "system"] as const;

export type WalterState = (typeof WALTER_STATES)[number];
export type Emotion = (typeof EMOTIONS)[number];
export type Environment = (typeof ENVIRONMENTS)[number];
export type DisplayRole = (typeof DISPLAY_ROLES)[number];
export type ToolType = (typeof TOOL_TYPES)[number];
export type WalterSurface = (typeof WALTER_SURFACES)[number];
export type ToolPhase = "idle" | "outgoing" | "running" | "returning" | "complete" | "failed" | "cancelled";
export type TransferDirection = "face-to-eye" | "eye-to-face" | "face-to-heart";
export type TransferPhase = "launch" | "transit" | "capture" | "resolve" | "return" | "impact" | "fault";
export type PerformanceMode = "auto" | "live" | "eco";
export type WalterMode = "booting" | "awake" | "sleeping" | "degraded";
export type WalterPhase = (typeof WALTER_PHASES)[number];
export type DeviceRole = (typeof DEVICE_ROLES)[number];
export type DeviceState = "online" | "offline" | "degraded" | "unknown";
export type ConnectionState = "standalone" | "connecting" | "connected" | "reconnecting" | "offline";
export type BrainStatus = "offline" | "connecting" | "ready" | "thinking" | "approval" | "error";
export type SystemAction = "reconnect-brain" | "restart-body" | "restart-display" | "export-support";

export interface Point { x: number; y: number }

export interface WalterMemory {
  id: string;
  text: string;
  kind: "fact" | "preference" | "moment";
  createdAt: number;
}

export interface WalterTimer {
  id: string;
  label: string;
  dueAt: number;
  status: "running" | "complete" | "cancelled";
}

export interface NewsSignal {
  id: string;
  source: string;
  title: string;
  detail: string;
  priority: "quiet" | "normal" | "urgent";
}

export interface WalterTransfer {
  id: string;
  kind: ToolType | "speech" | "wake";
  direction: TransferDirection;
  phase: TransferPhase;
  startedAt: number;
  durationMs: number;
  energy: number;
  progress: number | null;
  outcome: "pending" | "success" | "failed";
}

export interface WalterApproval {
  id: string;
  title: string;
  detail: string;
  risk: "confirm" | "blocked";
  action: SystemAction | null;
  createdAt: number;
}

export interface WalterSnapshot {
  protocol: 4;
  seq: number;
  sessionId: string;
  mode: WalterMode;
  phase: WalterPhase;
  state: WalterState;
  emotion: Emotion;
  audioLevel: number;
  gaze: Point;
  environment: Environment;
  view: { surface: WalterSurface; overlay: "none" | "conversation" | "controls" | "action-ring" | "approval"; brightness: number };
  brain: { status: BrainStatus; agentId: string; sessionKey: string | null; runId: string | null; stage: string; error: string | null };
  attention: { present: boolean; target: Point; confidence: number; lastSeenAt: number | null; wakeWord: boolean };
  audio: { micLevel: number; outputLevel: number; listening: boolean; muted: boolean; route: "none" | "local" | "talk"; partialTranscript: string; finalTranscript: string };
  vision: { privacy: "open" | "closed"; cameraStatus: DeviceState; tracking: boolean; target: Point; confidence: number; previewUrl: string | null; lastCaptureAt: number | null };
  tool: { type: ToolType; phase: ToolPhase; nonce: number; id: string | null; progress: number | null; error: string | null };
  transfer: WalterTransfer | null;
  input: { level: number; transcript: string; final: boolean };
  output: { level: number; text: string; speaking: boolean };
  task: { id: string | null; label: string; progress: number | null; error: string | null };
  conversation: WalterTurn[];
  memories: { items: WalterMemory[]; query: string };
  timers: { items: WalterTimer[] };
  news: { status: "idle" | "refreshing" | "ready" | "offline"; fetchedAt: number | null; stale: boolean; weather: string; signals: NewsSignal[] };
  settings: { volume: number; brightness: number; muted: boolean; cameraTracking: boolean; wakeWord: boolean; performanceMode: PerformanceMode };
  approval: WalterApproval | null;
  devices: Record<DeviceRole, DeviceState>;
  system: {
    online: boolean;
    cpuTemp: number | null;
    lowVoltage: boolean;
    memoryUsedMb: number | null;
    processRssMb: number | null;
    diskUsedPercent: number | null;
    load1: number | null;
    uptimeSeconds: number;
    daemonVersion: string;
    hostname: string;
    ipAddress: string | null;
    platform: string;
  };
  connection: { state: ConnectionState; reconnects: number; lastMessageAt: number | null };
  updatedAt: number;
}

export interface WalterTurn {
  id: string;
  user: string;
  walter: string;
  tool: ToolType | null;
  status: "complete" | "failed" | "cancelled";
  createdAt: number;
}

export type WalterCommand =
  | { type: "SET_STATE"; state: WalterState }
  | { type: "SET_EMOTION"; emotion: Emotion }
  | { type: "SET_AUDIO"; value: number }
  | { type: "SET_GAZE"; gaze: Point }
  | { type: "SET_ENVIRONMENT"; environment: Environment }
  | { type: "NAVIGATE"; surface: WalterSurface }
  | { type: "SET_OVERLAY"; overlay: WalterSnapshot["view"]["overlay"] }
  | { type: "START_LISTENING" }
  | { type: "STOP_LISTENING" }
  | { type: "SET_AUDIO_INPUT"; value: number; partial?: string }
  | { type: "SET_AUDIO_OUTPUT"; value: number }
  | { type: "SET_PRIVACY"; privacy: "open" | "closed" }
  | { type: "VISION_TRACK"; target: Point; confidence: number }
  | { type: "VISION_CAPTURE"; capturedAt?: number }
  | { type: "SET_ATTENTION"; present: boolean; target?: Point; confidence?: number }
  | { type: "MEMORY_SAVE"; text: string; kind?: WalterMemory["kind"] }
  | { type: "MEMORY_FORGET"; id: string }
  | { type: "MEMORY_QUERY"; query: string }
  | { type: "TIMER_CREATE"; label: string; durationMs: number }
  | { type: "TIMER_CANCEL"; id: string }
  | { type: "TIMER_COMPLETE"; id: string }
  | { type: "NEWS_REFRESH" }
  | { type: "NEWS_UPDATE"; weather: string; signals: NewsSignal[]; fetchedAt?: number }
  | { type: "SET_SETTING"; key: keyof WalterSnapshot["settings"]; value: number | boolean | PerformanceMode }
  | { type: "BRAIN_STATUS"; status: BrainStatus; stage?: string; error?: string | null; runId?: string | null }
  | { type: "BRAIN_STREAM"; text: string }
  | { type: "REQUEST_APPROVAL"; title: string; detail: string; risk?: WalterApproval["risk"]; action?: SystemAction }
  | { type: "RESOLVE_APPROVAL"; approved: boolean }
  | { type: "TOOL_START"; tool: ToolType }
  | { type: "TOOL_PROGRESS"; progress: number; label?: string }
  | { type: "TOOL_RETURN"; tool: ToolType }
  | { type: "TOOL_FAIL"; tool: ToolType; message: string }
  | { type: "CLEAR_TRANSFER"; id?: string }
  | { type: "BEGIN_TURN" }
  | { type: "SUBMIT_TEXT"; text: string }
  | { type: "COMPLETE_TURN"; response: string; tool?: ToolType | null }
  | { type: "FINISH_SPEAKING" }
  | { type: "CANCEL_TURN" }
  | { type: "SET_DEVICE"; device: DeviceRole; status: DeviceState }
  | { type: "SET_SYSTEM"; system: Partial<WalterSnapshot["system"]> }
  | { type: "WAKE" }
  | { type: "SLEEP" };

export interface WalterTransport {
  readonly authoritative?: boolean;
  publish(command: WalterCommand): void;
  subscribe(listener: (command: WalterCommand) => void): () => void;
  subscribeSnapshot?(listener: (snapshot: WalterSnapshot) => void): () => void;
  subscribeConnection?(listener: (state: ConnectionState) => void): () => void;
  close(): void;
}

export type WalterWireMessage =
  | { type: "hello"; protocol: 4; role: DisplayRole; clientId: string }
  | { type: "welcome"; protocol: 4; clientId: string; snapshot: WalterSnapshot }
  | { type: "command"; protocol: 4; command: WalterCommand }
  | { type: "snapshot"; protocol: 4; snapshot: WalterSnapshot }
  | { type: "ping"; protocol: 4; now: number }
  | { type: "pong"; protocol: 4; now: number }
  | { type: "error"; protocol: 4; message: string };
